import {useRef} from "react";
import {Breadcrumb, Button} from "react-bootstrap";
import DataTable from "react-data-table-component";
import {BsGlobe, BsPencilSquare} from "react-icons/bs";
import {CgPlayListAdd} from "react-icons/cg";
import {FaSearch} from "react-icons/fa";
import {MdDeleteOutline} from "react-icons/md";
import {Link, useNavigate} from "react-router-dom";
import {Input, InputGroup} from "rsuite";
import {FloatingButton} from "../../components";
import Layout from "../../components/layouts/layout";
import {Permission} from "../../configs/constants";
import ComponentGuard from "../../helpers/ComponentGuard";
import {useLang, useProductList} from "../../hooks";
import {useNotification} from "../../hooks/useNotification";

function ProductListPage() {
  const {t} = useLang();
  const {
    productList, pagination,
    changePagination, deleteProduct, searchByTerm, isLoading,
    error, filter, filterDispatch
  } = useProductList();
  const {notifyError, notifySuccess} = useNotification();
  const searchBoxRef = useRef();
  const navigate = useNavigate();

  if (error) {
    notifyError(error);
    setTimeout(() => navigate("/"), 200);
  }

  const handleDelete = async (id) => {
    try {
      await deleteProduct(id);
      notifySuccess({
        title: "Product removed successfully"
      });
    } catch (err) {
      notifyError({
        title: "Failed to remove product!",
        message: err.message
      });
    }
  };

  const columns = [
    {
      name: "#",
      cell: (row, index) => (pagination.page - 1) * pagination.limit + (index + 1), //RDT provides index by default
      width: "50px"
    },
    {
      name: t("products_manage_col_name"),
      selector: (row) => row.name,
      sortable: true
    },
    {
      name: t("products_manage_col_action"),
      selector: (m) => {
        return (
          <div className="d-flex gap-2">
            <ComponentGuard needsPermission={[Permission.ProductUpdate]}>
              <Link to={`/products/edit/${m.id}`} className="btn btn-secondary">
                <BsPencilSquare />
              </Link>
            </ComponentGuard>
            <ComponentGuard needsPermission={[Permission.ProductDestroy]}>
              <Button variant="danger" onClick={() => handleDelete(m.id)}>
                <MdDeleteOutline />
              </Button>
            </ComponentGuard>
            <ComponentGuard needsPermission={[Permission.ProductLanguagesIndex]}>
              <Link to={`/langs/product/${m.id}`} className="btn btn-info">
                <BsGlobe />
              </Link>
            </ComponentGuard>
          </div>
        );
      }
    }
  ];

  return (
    <>
      <Layout>
        <header>
          <div className="d-flex justify-content-between">
            <div>
              <Breadcrumb>
                <Breadcrumb.Item active>
                  {t("products_manage_products")}
                </Breadcrumb.Item>
              </Breadcrumb>
            </div>
          </div>
        </header>
        <section id="albumList" className="mb-5 pb-5">
          <h2>{t("products_manage_productsList")}</h2>
          <DataTable
            columns={columns}
            data={productList.products}
            progressPending={isLoading}
            pagination
            paginationServer
            paginationComponentOptions={{noRowsPerPage: true}}
            paginationTotalRows={productList.total}
            onChangePage={(page) => {
              changePagination({page});
            }}
            fixedHeader
            fixedHeaderScrollHeight="800px"
            highlightOnHover
            subHeader
            subHeaderComponent={
              <InputGroup className="mb-3" style={{width: "300px"}}>
                <Input ref={searchBoxRef} placeholder={t("products_manage_searchByName")} />
                <InputGroup.Button onClick={() => filterDispatch({
                  type: "set",
                  payload: {key: "term", value: searchBoxRef.current.value}
                })}>
                  <FaSearch />
                </InputGroup.Button>
              </InputGroup>
            }
            subHeaderAlign="right"
          />
        </section>
      </Layout>
      <ComponentGuard needsPermission={[Permission.ProductStore]}>
        <FloatingButton
          onClick={() => {
            navigate("/products/new");
          }}
          icon={<CgPlayListAdd />}
          name={t("products_manage_newProduct")}
        />
      </ComponentGuard>
    </>
  );
}

export default ProductListPage;

